import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import cx from 'classnames';

import { useWordSearchService } from 'hooks/api/agents/content/word-search';
import { addManyWordSearchesToStore } from 'store/actions/word-search';
import { formatDateTime } from '../../jobs/config/job';
import { STATUS_LABELS, resolveWordSearchTitle } from '../config/word-search';
import styles from './list.module.css';

const PAGE_SIZE = 15;
const SEARCH_DELAY = 400;

export const WordSearchList = ({
  selected_id,
  refresh_key = 0,
  onSelect = () => {},
  onRefreshComplete = () => {}
}) => {
  const dispatch = useDispatch();
  const { fetchWordSearches, searchWordSearches } = useWordSearchService();
  const word_searches_in_store = useSelector((state) => state.word_searches);

  const [page_ids, setPageIds] = useState([]);
  const [size, setSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => handleDataRequest(), query ? SEARCH_DELAY : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query]);

  useEffect(() => {
    if (!refresh_key) return;
    handleDataRequest().then(onRefreshComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh_key]);

  const handleDataRequest = async () => {
    setLoading(true);
    const query_string = `page=${page}&population=${PAGE_SIZE}&sort_by=-created_on`;
    const keyword = query.trim();
    const { word_searches, size } = keyword
      ? await searchWordSearches('theme', keyword, { query_string })
      : await fetchWordSearches({ query_string });

    dispatch(addManyWordSearchesToStore(word_searches));
    setPageIds(word_searches.map(({ id }) => id));
    setSize(size);
    setLoading(false);
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setPage(0);
  };

  const visible = page_ids.map((id) => word_searches_in_store[id]).filter(Boolean);
  const can_prev = page > 0;
  const can_next = (page + 1) * PAGE_SIZE < size;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <path
              d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
              fill="currentColor"
            />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by theme..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
          />
          {query && (
            <button className={styles.searchClear} onClick={() => handleQueryChange('')}>
              &#x2715;
            </button>
          )}
        </div>
      </div>

      <div className={styles.list}>
        {loading && visible.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton height={13} width="70%" style={{ marginBottom: 6 }} borderRadius={4} />
              <Skeleton height={10} width="38%" borderRadius={4} />
            </div>
          ))
        ) : visible.length === 0 ? (
          <div className={styles.listEmpty}>
            {query ? `No results for "${query}"` : 'No word searches yet'}
          </div>
        ) : (
          visible.map((word_search) => (
            <div
              key={word_search.id}
              className={cx(styles.item, selected_id === word_search.id && styles.itemSelected)}
              onClick={() => onSelect(word_search.id)}
            >
              <div className={styles.itemTitle}>{resolveWordSearchTitle(word_search)}</div>
              <p className={styles.itemMeta}>
                {word_search.word_count} words
                {word_search.grid_size
                  ? ` · ${word_search.grid_size}×${word_search.grid_size}`
                  : ' · auto size'}
              </p>
              <div className={styles.itemFooter}>
                <span className={cx(styles.statusBadge, styles[word_search.status])}>
                  {STATUS_LABELS[word_search.status] || word_search.status}
                </span>
                {word_search.created_on && (
                  <span className={styles.itemDate}>{formatDateTime(word_search.created_on)}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.pagination}>
        <button
          className={styles.pageBtn}
          disabled={!can_prev}
          onClick={() => can_prev && setPage((p) => p - 1)}
        >
          &#8249;
        </button>
        <span className={styles.pageLabel}>Page {page + 1}</span>
        <button
          className={styles.pageBtn}
          disabled={!can_next}
          onClick={() => can_next && setPage((p) => p + 1)}
        >
          &#8250;
        </button>
      </div>
    </div>
  );
};
